'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EMarketOrderType, MarketOrderType } from '@/lib/types/market-order-type';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { cn } from '@/lib/ui-kit/utils';

type MarketOrderTypeSelectProps = {
  className?: string;
  value?: MarketOrderType;
  onValueChange?: (value: MarketOrderType) => void;
};

// Debounce closing on mouse-leave so the cursor can travel from the trigger
// to the floating content (and through the small gap between them).
const HOVER_CLOSE_DELAY_MS = 150;

export const MarketOrderTypeSelect = ({
  value = EMarketOrderType.MARKET,
  onValueChange,
  className,
}: MarketOrderTypeSelectProps) => {
  const t = useTranslations('orderForm');
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const options = useMemo(
    () => [
      { value: EMarketOrderType.MARKET, label: t('market') },
      { value: EMarketOrderType.LIMIT, label: t('limit') },
    ],
    [t],
  );

  const activeLabel = options.find((o) => o.value === value)?.label ?? options[0].label;

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleToggle = () => {
    const next = options.find((o) => o.value !== value) ?? options[0];
    onValueChange?.(next.value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          // Block Popover's default open-on-click; clicks should toggle value.
          onPointerDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            handleToggle();
          }}
          className={cn(
            'flex cursor-pointer items-center font-medium text-[13px] text-v2-text-primary leading-4 tracking-[-0.13px] transition-opacity hover:opacity-80',
            className,
          )}
        >
          {activeLabel}
          <Icon
            name="ArrowSmallDown"
            size={24}
            className={cn(
              'shrink-0 opacity-50 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={0}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="flex w-auto min-w-[120px] flex-col gap-0 rounded-[10px] border-none bg-v2-surface-secondary p-1 shadow-[1px_1px_8px_0px_rgba(0,0,0,0.2)]"
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onValueChange?.(opt.value);
              setOpen(false);
            }}
            className={cn(
              'flex w-full cursor-pointer items-center rounded-[8px] px-3 py-2 text-left font-normal text-[13px] leading-4 tracking-[-0.13px] transition-colors hover:bg-v2-btn-secondary',
              opt.value === value ? 'text-v2-text-primary' : 'text-v2-text-secondary',
            )}
          >
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
